import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useMemo, useEffect } from 'react'
import { usePlayerStore } from '@/store/playerStore'
import { mockPlayers, getUniqueTeams } from '@/data/mockData'
import { PlayerCard } from '@/components/PlayerCard'

export default function Home() {
  const { players, setPlayers } = usePlayerStore()

  // Load mock data
  useEffect(() => {
    if (players.length === 0) {
      setPlayers(mockPlayers)
    }
  }, [players.length, setPlayers])

  // Calculate stats
  const stats = useMemo(() => {
    const uniqueTeams = getUniqueTeams(players)
    const retiredCount = players.filter(p => p.status_2026 === 'RETIRED').length

    return {
      totalPlayers: players.length,
      totalTeams: uniqueTeams.length,
      retiredPlayers: retiredCount || Math.floor(players.length * 0.3), // Estimate if not available
    }
  }, [players])

  // Get position distribution
  const positionStats = useMemo(() => {
    const counts: Record<string, number> = {}
    players.forEach(p => {
      if (p.position) {
        counts[p.position] = (counts[p.position] || 0) + 1
      }
    })
    return counts
  }, [players])

  // Featured players (first 4)
  const featuredPlayers = useMemo(() => players.slice(0, 4), [players])

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-4xl mx-auto"
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-tertiary bg-clip-text text-transparent leading-tight">
            LPL 选手追踪器
          </h1>
          <p className="text-lg md:text-xl text-secondary mb-6 leading-relaxed">
            追踪 2025 年活跃，却在 2026 年消失在赛场上的 LPL 选手
          </p>
          <p className="text-tertiary mb-10 max-w-2xl mx-auto">
            记录每一个离场的背影，致敬每一段职业生涯
          </p>
          <Link
            to="/players"
            className="inline-block px-8 py-3 bg-surface hover:bg-border border border-border rounded-full text-primary font-medium transition-all duration-200 hover:scale-105"
          >
            浏览全部选手
          </Link>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto"
        >
          <div className="bg-surface border border-border rounded-2xl p-6 text-center">
            <div className="text-4xl font-bold text-primary mb-2">
              {stats.totalPlayers}
            </div>
            <div className="text-secondary text-sm">离场选手</div>
          </div>
          <div className="bg-surface border border-border rounded-2xl p-6 text-center">
            <div className="text-4xl font-bold text-primary mb-2">
              {stats.totalTeams}
            </div>
            <div className="text-secondary text-sm">涉及战队</div>
          </div>
          <div className="bg-surface border border-border rounded-2xl p-6 text-center">
            <div className="text-4xl font-bold text-status-retired mb-2">
              {stats.retiredPlayers}
            </div>
            <div className="text-secondary text-sm">退役选手</div>
          </div>
        </motion.div>
      </section>

      {/* Position Distribution */}
      <section className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <h2 className="text-2xl font-bold text-primary mb-6 text-center">
            位置分布
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            {Object.entries(positionStats).map(([position, count]) => {
              const colors: Record<string, string> = {
                TOP: '#ff6b6b',
                JUNGLE: '#4ecdc4',
                MID: '#ffe66d',
                ADC: '#ff6b9d',
                SUPPORT: '#95e1d3',
              }
              const names: Record<string, string> = {
                TOP: '上单',
                JUNGLE: '打野',
                MID: '中单',
                ADC: 'ADC',
                SUPPORT: '辅助',
              }
              return (
                <div
                  key={position}
                  className="flex items-center gap-2 px-4 py-2 bg-surface border border-border rounded-full"
                >
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: colors[position] }}
                  />
                  <span className="text-secondary text-sm">
                    {names[position]}: {count}
                  </span>
                </div>
              )
            })}
          </div>
        </motion.div>
      </section>

      {/* Featured Players */}
      {featuredPlayers.length > 0 && (
        <section className="container mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            <div className="flex items-center justify-between mb-8 max-w-6xl mx-auto">
              <h2 className="text-2xl font-bold text-primary">精选选手</h2>
              <Link
                to="/players"
                className="text-secondary hover:text-primary text-sm transition-colors"
              >
                查看全部 →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {featuredPlayers.map((player, index) => (
                <motion.div
                  key={player.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                >
                  <Link to="/players">
                    <PlayerCard player={player} />
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>
      )}

      {/* Footer CTA */}
      <section className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center"
        >
          <p className="text-tertiary mb-6">
            数据持续更新中，来自 LPL 官方与社区贡献
          </p>
          <Link
            to="/about"
            className="text-secondary hover:text-primary text-sm transition-colors"
          >
            了解更多 →
          </Link>
        </motion.div>
      </section>
    </div>
  )
}
